import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useLocalSearchParams } from 'expo-router'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useMutation } from '@/lib/mutations'
import { newRecordId, useStore } from '@/lib/pocketbase'
import { normalizeTagPayload } from '@/lib/tags'
import type { TagFormData } from '@/types/tags'

export function useTags(orgSlug?: string) {
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const slug = orgSlug ?? params.orgSlug

    const [tagsCollection, orgsCollection] = useStore('tags', 'orgs')

    const { isLoading, data = [] } = useLiveQuery(
        q =>
            slug
                ? q
                      .from({ org: orgsCollection })
                      .where(({ org }) => eq(org.slug, slug))
                      .join({ tag: tagsCollection }, ({ org, tag }) => eq(tag.org, org.id))
                      .orderBy(({ tag }) => tag?.created ?? '', 'desc')
                      .select(({ tag }) => ({ tag }))
                : undefined,
        [slug]
    )

    const tags = data.map(d => d.tag).filter((t): t is NonNullable<typeof t> => !!t)

    return {
        tags,
        isPending: isLoading,
    }
}

export function useCreateTag() {
    const { org: currentOrg } = useCurrentOrg()
    const [tagsCollection] = useStore('tags')

    const mutation = useMutation({
        mutationFn: function* (data: TagFormData) {
            if (!currentOrg) throw new Error('No current organization found')

            const tagId = newRecordId()

            yield tagsCollection.insert({
                ...normalizeTagPayload(data),
                id: tagId,
                org: currentOrg.id,
            })

            return { tagId }
        },
    })

    return {
        createTag: mutation.mutate,
        isCreating: mutation.isPending,
        mutation,
    }
}

export function useUpdateTag() {
    const [tagsCollection] = useStore('tags')

    const mutation = useMutation({
        mutationFn: function* ({ tagId, data }: { tagId: string; data: TagFormData }) {
            yield tagsCollection.update(tagId, draft => {
                Object.assign(draft, normalizeTagPayload(data))
            })
        },
    })

    return {
        updateTag: mutation.mutate,
        isUpdating: mutation.isPending,
        mutation,
    }
}

export function useDeleteTag() {
    const [tagsCollection] = useStore('tags')

    const mutation = useMutation({
        mutationFn: function* (tagId: string) {
            yield tagsCollection.delete(tagId)
        },
    })

    return {
        deleteTag: mutation.mutate,
        isDeleting: mutation.isPending,
        mutation,
    }
}
